import { buttonStates } from "./buttonStates";
import { V2 } from "../theme/DesignV2";
import React, { JSX } from "react";
import { useTheme } from "styled-components";
import { Box } from "../box/Box";
import Spacer from "../spacer/Spacer";
import { ITheme, RecursiveCSS } from "../theme/GlobalTheme";

export type TButtons = typeof getDefaultButtons;
export type TButtonVariant = keyof TButtons;

const DISABLED_OPACITY = 0.55;

const defaultButton: RecursiveCSS = {
	padding: "9px 14px", minHeight: V2.controlHeight, borderRadius: V2.controlRadius,
	display: "flex", alignItems: "center", position: "relative", gap: "8px",
	fontWeight: "600", userSelect: "none", fontSize: "14px", justifyContent: "center",
	whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden",
	border: "none", fontFamily: "inherit",
	backgroundColor: "buttonBackgroundPrimary", color: "buttonTextPrimary",
};
const secondary: RecursiveCSS = {
	...defaultButton, backgroundColor: "#FFFFFF", border: "1px solid #D7DEE8", color: "buttonTextSecondary",
};
const empty: RecursiveCSS = {
	...defaultButton, backgroundColor: "transparent", color: "buttonTextSecondary",
};
const small: RecursiveCSS = {
	minHeight: "36px", "@media (pointer: coarse)": { minHeight: V2.controlHeight },
	fontSize: ".84rem", padding: "9px 14px",
};
export const getDefaultButtons = {
	primary: buttonStates(defaultButton),
	secondary: buttonStates(secondary),
	empty: buttonStates(empty),
	icon: buttonStates({ ...empty, borderRadius: "8px", minHeight: 0, padding: "0px" }),
	primarySmall: buttonStates({ ...defaultButton, ...small }),
	dangerSmall: buttonStates({ ...defaultButton, ...small, backgroundColor: "#EA3568", color: "#FFFFFF" }),
	secondarySmall: buttonStates({ ...secondary, ...small }),
	emptySmall: buttonStates({ ...empty, ...small }),
};

interface IButtonProps {
	onClick: (e?: any) => void;
	children?: any;
	title?: string;
	variant?: TButtonVariant;
	sx?: React.CSSProperties & { [key: string]: any };
	iconRight?: JSX.Element;
	size?: "small" | "medium" | "large";
	disabled?: boolean;
	warning?: boolean;
	color?: string;
	decorator?: "form";
	icon?: JSX.Element;
	center?: boolean;
	ariaLabel?: string;
}

const Button = ({ onClick, icon, children, variant, sx, iconRight, disabled, title, center, ariaLabel }: IButtonProps) => {
	if (!variant && icon && !title && !children) variant = "icon";

	if (!variant) variant = "primary";

	let tsx = {} as React.CSSProperties;

	if (!children && title) children = title;
	let content = children;

	const theme = useTheme() as ITheme;
	if (theme && theme.buttons && theme.buttons[variant]) tsx = theme.buttons[variant];

	return (
		<Box
			element='button'
			type='button'
			data-ui-button={variant}
			disabled={disabled}
			aria-label={ariaLabel ?? title}
			sx={buttonStates({
				...tsx,
				...sx,
				pointerEvents: "auto",
				opacity: disabled ? DISABLED_OPACITY : 1,
				cursor: disabled ? "not-allowed" : "pointer",
			})}
			onClick={onClick}>
			{icon && (
				<Box
					element='span'
					sx={{
						alignItems: "center",
						lineHeight: "0px",
					}}>
					{icon}
				</Box>
			)}

			{content}

			{iconRight && (
				<>
					{!center && <Spacer grow element='span' />}
					<Box element='span' sx={{ lineHeight: "0px" }}>{iconRight}</Box>
				</>
			)}
		</Box>
	);
};

export default Button;
